import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  orcamentosApi,
  type DescontoEntrada,
  type ItemOrcamentoEntrada,
  type OrcamentoResumo,
  type StatusOrcamento,
  type TipoDesconto,
  type TipoItemOrcamento,
} from '@/features/orcamentos/api/orcamentosApi';
import { ehTerminal } from '@/features/orcamentos/api/etapasKanban';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import {
  clientesApi,
  type AtualizarClienteRequest,
  type ClienteResumo,
} from '@/features/clientes/api/clientesApi';
import { ClienteFormModal } from '@/features/clientes/pages/ClienteFormModal';
import {
  BotaoCompletarCliente,
  cadastroClienteIncompleto,
  type ClienteParaCompletar,
} from '@/features/clientes/components/BotaoCompletarCliente';
import {
  produtosApi,
  type ProdutoResumo,
} from '@/features/produtos/api/produtosApi';
import { servicosApi, type ServicoResumo } from '@/features/servicos/api/servicosApi';
import { veiculosApi, type VeiculoResumo } from '@/features/veiculos/api/veiculosApi';
import { colaboradoresApi, type ColaboradorResumo } from '@/features/colaboradores/api/colaboradoresApi';
import { Button } from '@/shared/ui/Button';
import { Field, MarcaObrigatorio } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { Toast } from '@/shared/ui/Toast';
import { IconSearch } from '@/shared/ui/Icon';
import { useEstaOnline } from '@/features/auth/AuthContext';
import {
  ItemTypeahead,
  type ItemTypeaheadValor,
  type OpcaoTypeahead,
} from '@/shared/ui/ItemTypeahead';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import {
  lerRascunhoVenda,
  limparRascunhoVenda,
  useSalvarRascunhoVenda,
} from '@/features/orcamentos/hooks/useRascunhoVenda';
import { useSessaoCaptura } from '@/features/captura/hooks/useSessaoCaptura';
import { aplicarCamposCapturados } from '@/features/captura/aplicarCamposCapturados';
import { vincularSessoesAoOrcamento } from '@/features/captura/vinculosPendentes';
import type { CamposCapturaDTO, ModoCaptura } from '@/features/captura/tipos';
import {
  BotaoCameraPlaca,
  type StatusLeituraPlaca,
} from '@/features/captura/components/BotaoCameraPlaca';
import { ModalCapturaCelular } from '@/features/captura/components/ModalCapturaCelular';
import { FaixaFotosCaptura } from '@/features/captura/components/FaixaFotosCaptura';
import { GaleriaFotosOS } from '@/features/captura/components/GaleriaFotosOS';

type Props = {
  aoCancelar: () => void;
  aoSalvar: () => Promise<void>;
  /** Quando informado, a tela carrega o orçamento existente e salva via PUT. */
  idEdicao?: string;
};

type LinhaItem = {
  uid: string;
  tipo: TipoItemOrcamento;
  selecao: ItemTypeaheadValor | null;
  quantidade: number;
  precoUnitario: number;
  descontoTipo: TipoDesconto;
  descontoValor: number;
  colaborador: ItemTypeaheadValor | null;
};

type DadosVeiculoNovo = {
  marca: string;
  modelo: string;
  ano: string;
  motor: string;
  km: string;
};

const DADOS_VEICULO_VAZIO: DadosVeiculoNovo = { marca: '', modelo: '', ano: '', motor: '', km: '' };

/** Snapshot do formulário guardado em localStorage (rascunho automático). */
type SnapshotVenda = {
  v: 1;
  clienteSelecao: ItemTypeaheadValor | null;
  placaSelecao: ItemTypeaheadValor | null;
  placaExibicao: string;
  dadosVeiculo: DadosVeiculoNovo;
  semPlaca: boolean;
  prazoEntrega: string;
  observacoes: string;
  colaboradorResponsavel: ItemTypeaheadValor | null;
  kmRegistrado: string;
  pecas: LinhaItem[];
  servicos: LinhaItem[];
  descontoGeralTipo: TipoDesconto;
  descontoGeralValor: number;
};

const formatadorMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

function diasFrenteIso(dias: number) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

const formatadorDataCurta = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
function formatarDataCurta(iso: string): string {
  return formatadorDataCurta.format(new Date(iso));
}
function diasAtras(iso: string): string {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  return `há ${dias} dias`;
}

function novaLinha(tipo: TipoItemOrcamento, colaborador: ItemTypeaheadValor | null = null): LinhaItem {
  return {
    uid: crypto.randomUUID(),
    tipo,
    selecao: null,
    quantidade: 1,
    precoUnitario: 0,
    descontoTipo: 'Percentual',
    descontoValor: 0,
    colaborador,
  };
}

function calcularDesconto(base: number, tipo: TipoDesconto, valor: number): number {
  if (valor <= 0) return 0;
  const bruto = tipo === 'Percentual' ? (base * valor) / 100 : valor;
  return Math.min(bruto, base);
}

function arred(valor: number): number {
  return Math.round(valor * 100) / 100;
}

function inteiroNaoNegativo(valor: number): number {
  return Math.max(0, Math.trunc(valor || 0));
}

function normalizarPlaca(p: string): string {
  return p.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
}

function normalizarChave(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
}

function toOpcaoVeiculo(v: VeiculoResumo): OpcaoTypeahead {
  const detalhe = `${v.marca} ${v.modelo}`.trim();
  // Sem placa, `descricao` já é "Marca Modelo Ano" — repetir o detalhe ao lado
  // deixaria o rótulo redundante.
  const rotulo = v.placa
    ? detalhe
      ? `${v.placa} — ${detalhe}`
      : v.placa
    : v.descricao;
  return {
    id: v.id,
    rotulo,
    busca: `${v.placa ?? ''} ${detalhe} ${v.ano ?? ''}`.trim(),
  };
}

export function OrcamentoNovoPage({ aoCancelar, aoSalvar, idEdicao }: Props) {
  const ehEdicao = Boolean(idEdicao);
  const estaOnline = useEstaOnline();
  const dialogos = useDialogos();

  const [clienteSelecao, setClienteSelecao] = useState<ItemTypeaheadValor | null>(null);
  const [placaSelecao, setPlacaSelecao] = useState<ItemTypeaheadValor | null>(null);
  const [placaExibicao, setPlacaExibicao] = useState('');
  const [dadosVeiculo, setDadosVeiculo] = useState<DadosVeiculoNovo>(DADOS_VEICULO_VAZIO);
  const [semPlaca, setSemPlaca] = useState(false);

  const [prazoEntrega, setPrazoEntrega] = useState(diasFrenteIso(30));
  const [observacoes, setObservacoes] = useState('');
  const [colaboradorResponsavel, setColaboradorResponsavel] = useState<ItemTypeaheadValor | null>(null);
  const [kmRegistrado, setKmRegistrado] = useState('');

  const [pecas, setPecas] = useState<LinhaItem[]>([novaLinha('Produto')]);
  const [servicos, setServicos] = useState<LinhaItem[]>([novaLinha('Servico')]);

  const [descontoGeralTipo, setDescontoGeralTipo] = useState<TipoDesconto>('Percentual');
  const [descontoGeralValor, setDescontoGeralValor] = useState(0);

  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [opcoesClientes, setOpcoesClientes] = useState<OpcaoTypeahead[]>([]);
  const [opcoesColaboradores, setOpcoesColaboradores] = useState<OpcaoTypeahead[]>([]);
  const [opcoesProdutos, setOpcoesProdutos] = useState<OpcaoTypeahead[]>([]);
  const [opcoesServicos, setOpcoesServicos] = useState<OpcaoTypeahead[]>([]);

  // Modal "completar cadastro" do cliente escolhido (só dados, sem veículos).
  const [completandoCliente, setCompletandoCliente] = useState(false);
  const idTituloVeiculo = useId();

  const [veiculosCliente, setVeiculosCliente] = useState<VeiculoResumo[]>([]);
  const [veiculosBuscados, setVeiculosBuscados] = useState<VeiculoResumo[]>([]);
  const [carregandoVeiculos, setCarregandoVeiculos] = useState(false);
  const [avisoCliente, setAvisoCliente] = useState<string | null>(null);

  const [salvando, setSalvando] = useState(false);
  // Carga inicial (listas + venda em edição) concluída. Dispara a checagem
  // de rascunho recuperável.
  const [carregadoInicial, setCarregadoInicial] = useState(false);
  // Liga o autosave do rascunho só depois que a decisão de recuperar/descartar
  // foi tomada — senão o estado inicial sobrescreveria o rascunho guardado.
  const [rascunhoAtivo, setRascunhoAtivo] = useState(false);
  // Status do orçamento (só em edição) — alterado direto via API, separado
  // do "Salvar alterações".
  const [status, setStatus] = useState<StatusOrcamento | null>(null);
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const { etapas, rotuloDe } = useEtapasKanban();
  // Estado da consulta automática de placa (cache central + wdapi2 no servidor).
  const [consultandoPlaca, setConsultandoPlaca] = useState(false);
  const [avisoConsultaPlaca, setAvisoConsultaPlaca] = useState<
    { tipo: 'ok' | 'erro'; mensagem: string } | null
  >(null);

  /**
   * `placaInformada`: placa que acabou de chegar (câmera do celular) e
   * ainda não está no estado — o setState só vale no próximo render.
   */
  async function consultarPlacaAtual(placaInformada?: string): Promise<void> {
    const origem =
      placaInformada ?? (placaSelecao?.tipo === 'novo' ? placaSelecao.descricao : null);
    if (origem === null) return;
    const placa = normalizarPlaca(origem);
    if (placa.length !== 7) {
      setAvisoConsultaPlaca({ tipo: 'erro', mensagem: 'Digite a placa antes de consultar.' });
      return;
    }
    setConsultandoPlaca(true);
    setAvisoConsultaPlaca(null);
    try {
      const r = await veiculosApi.consultarPlaca(placa);
      setDadosVeiculo((d) => ({
        marca: r.veiculo.marca || d.marca,
        modelo: r.veiculo.modelo || d.modelo,
        ano: r.veiculo.ano || d.ano,
        motor: r.veiculo.motor || d.motor,
        km: d.km,
      }));
      setAvisoConsultaPlaca({
        tipo: 'ok',
        mensagem:
          r.origem === 'cache'
            ? 'Dados do cache (sem custo).'
            : 'Placa consultada (1ª vez — fica salva para as próximas).',
      });
    } catch (e) {
      setAvisoConsultaPlaca({
        tipo: 'erro',
        mensagem: e instanceof Error ? e.message : 'Falha ao consultar a placa.',
      });
    } finally {
      setConsultandoPlaca(false);
    }
  }
  const [erro, setErro] = useState<string | null>(null);

  // ---- Captura pelo celular (placa + fotos) ----
  // Nº da O.S. em edição: vai para o cabeçalho da página do celular.
  const [numeroEdicao, setNumeroEdicao] = useState<string | null>(null);
  // Modal do QR aberto (e em que aba o celular começa). Só na nova O.S.
  const [modoQr, setModoQr] = useState<ModoCaptura | null>(null);
  const [statusLeituraPlaca, setStatusLeituraPlaca] = useState<StatusLeituraPlaca>(null);
  const [avisoCaptura, setAvisoCaptura] = useState<string | null>(null);
  const fecharAvisoCaptura = useCallback(() => setAvisoCaptura(null), []);
  // A sessão vive enquanto o formulário estiver aberto: o mesmo QR serve
  // para a placa e para as fotos, que são vinculadas à O.S. ao salvar.
  const captura = useSessaoCaptura({
    aoReceberCampos: (campos) => receberCamposDoCelular(campos),
  });

  const buscaPlacaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ultimoResponsavelAplicadoRef = useRef<ItemTypeaheadValor | null>(null);

  useEffect(() => {
    void Promise.all([
      clientesApi.listar(),
      produtosApi.listar(),
      servicosApi.listar(),
      colaboradoresApi.listar(),
    ])
      .then(([cs, ps, ss, cols]) => {
        setClientes(cs);
        setOpcoesClientes(cs.map(toOpcaoCliente));
        setOpcoesColaboradores(cols.filter((c) => c.ativo).map(toOpcaoColaborador));
        const opcProd = ps
          .filter((p) => p.ativo)
          .sort((a, b) => ordenarPorNomeECodigo(a.nome, a.codigo, b.nome, b.codigo))
          .map(toOpcaoProduto);
        const opcServ = ss
          .filter((s) => s.ativo)
          .sort((a, b) => ordenarPorNomeECodigo(a.nome, a.codigo, b.nome, b.codigo))
          .map(toOpcaoServico);
        setOpcoesProdutos(opcProd);
        setOpcoesServicos(opcServ);

        if (idEdicao) {
          void orcamentosApi
            .obter(idEdicao)
            .then((d) => {
              const cli = cs.find((c) => c.id === d.clienteId);
              if (cli) setClienteSelecao({ tipo: 'existente', opcao: toOpcaoCliente(cli) });
              const colaboradorResponsavelExistente = cols.find(
                (c) => c.id === d.colaboradorResponsavelId,
              );
              if (colaboradorResponsavelExistente) {
                const responsavelCarregado: ItemTypeaheadValor = {
                  tipo: 'existente',
                  opcao: toOpcaoColaborador(colaboradorResponsavelExistente),
                };
                setColaboradorResponsavel(responsavelCarregado);
                ultimoResponsavelAplicadoRef.current = responsavelCarregado;
              }
              setPlacaExibicao(d.placa ?? '');
              setNumeroEdicao(d.numero ?? null);
              setStatus(d.status);
              setPrazoEntrega(new Date(d.prazoEntrega).toISOString().slice(0, 10));
              setObservacoes(d.observacoes ?? '');
              setDescontoGeralTipo(d.descontoGeralTipo ?? 'Percentual');
              setDescontoGeralValor(d.descontoGeralValor ?? 0);

              const linhasPecas: LinhaItem[] = [];
              const linhasServicos: LinhaItem[] = [];
              for (const it of d.itens) {
                const opc = (it.tipo === 'Produto' ? opcProd : opcServ).find(
                  (o) => o.id === it.referenciaId,
                );
                const colaboradorItem = cols.find((c) => c.id === it.colaboradorId);
                const linha: LinhaItem = {
                  uid: crypto.randomUUID(),
                  tipo: it.tipo,
                  selecao: opc
                    ? { tipo: 'existente', opcao: opc }
                    : { tipo: 'novo', descricao: it.descricao },
                  quantidade: it.quantidade,
                  precoUnitario: it.precoUnitario,
                  descontoTipo: it.descontoTipo ?? 'Percentual',
                  descontoValor: it.descontoValor ?? 0,
                  colaborador: colaboradorItem
                    ? { tipo: 'existente', opcao: toOpcaoColaborador(colaboradorItem) }
                    : null,
                };
                (it.tipo === 'Produto' ? linhasPecas : linhasServicos).push(linha);
              }
              setPecas(linhasPecas.length ? linhasPecas : [novaLinha('Produto')]);
              setServicos(linhasServicos.length ? linhasServicos : [novaLinha('Servico')]);
            })
            .catch((e: unknown) =>
              setErro(e instanceof Error ? e.message : 'Falha ao carregar ordem de serviço.'),
            )
            .finally(() => setCarregadoInicial(true));
        } else {
          setCarregadoInicial(true);
        }
      })
      .catch((e: unknown) => {
        setErro(e instanceof Error ? e.message : 'Falha ao carregar listas.');
        setCarregadoInicial(true);
      });
  }, [idEdicao]);

  // ---- Rascunho automático (localStorage) ----
  const snapshotRascunho = useMemo<SnapshotVenda>(
    () => ({
      v: 1,
      clienteSelecao,
      placaSelecao,
      placaExibicao,
      dadosVeiculo,
      semPlaca,
      prazoEntrega,
      observacoes,
      colaboradorResponsavel,
      kmRegistrado,
      pecas,
      servicos,
      descontoGeralTipo,
      descontoGeralValor,
    }),
    [
      clienteSelecao,
      placaSelecao,
      placaExibicao,
      dadosVeiculo,
      semPlaca,
      prazoEntrega,
      observacoes,
      colaboradorResponsavel,
      kmRegistrado,
      pecas,
      servicos,
      descontoGeralTipo,
      descontoGeralValor,
    ],
  );

  useSalvarRascunhoVenda(idEdicao, snapshotRascunho, rascunhoAtivo);

  function aplicarSnapshot(s: SnapshotVenda) {
    setClienteSelecao(s.clienteSelecao);
    setPlacaSelecao(s.placaSelecao);
    setPlacaExibicao(s.placaExibicao ?? '');
    setDadosVeiculo(s.dadosVeiculo ?? DADOS_VEICULO_VAZIO);
    setSemPlaca(Boolean(s.semPlaca));
    if (s.prazoEntrega) setPrazoEntrega(s.prazoEntrega);
    setObservacoes(s.observacoes ?? '');
    setColaboradorResponsavel(s.colaboradorResponsavel);
    ultimoResponsavelAplicadoRef.current = s.colaboradorResponsavel;
    setKmRegistrado(s.kmRegistrado ?? '');
    setPecas(s.pecas?.length ? s.pecas : [novaLinha('Produto')]);
    setServicos(s.servicos?.length ? s.servicos : [novaLinha('Servico')]);
    setDescontoGeralTipo(s.descontoGeralTipo ?? 'Percentual');
    setDescontoGeralValor(s.descontoGeralValor ?? 0);
  }

  // Após a carga inicial, oferece recuperar um rascunho não-salvo (se houver).
  // O autosave (`rascunhoAtivo`) só liga depois da decisão para não
  // sobrescrever o rascunho com o estado recém-carregado.
  useEffect(() => {
    if (!carregadoInicial) return;
    const rascunho = lerRascunhoVenda<SnapshotVenda>(idEdicao);
    if (!rascunho || rascunho.v !== 1) {
      setRascunhoAtivo(true);
      return;
    }
    let cancelado = false;
    void (async () => {
      const ok = await dialogos.confirmar({
        titulo: 'Recuperar rascunho?',
        mensagem: ehEdicao
          ? 'Há alterações não salvas desta ordem de serviço guardadas neste computador. Deseja recuperá-las?'
          : 'Há um rascunho de ordem de serviço não salvo neste computador. Deseja recuperá-lo?',
        textoConfirmar: 'Recuperar',
        textoCancelar: 'Descartar',
      });
      if (cancelado) return;
      if (ok) aplicarSnapshot(rascunho);
      else limparRascunhoVenda(idEdicao);
      setRascunhoAtivo(true);
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregadoInicial]);

  const clienteIdSelecionado =
    clienteSelecao?.tipo === 'existente' ? clienteSelecao.opcao.id : '';

  useEffect(() => {
    if (clienteIdSelecionado === '') {
      setVeiculosCliente([]);
      setCarregandoVeiculos(false);
      return;
    }
    setCarregandoVeiculos(true);
    void veiculosApi
      .listarPorCliente(clienteIdSelecionado)
      .then(setVeiculosCliente)
      .catch(() => setVeiculosCliente([]))
      .finally(() => setCarregandoVeiculos(false));
  }, [clienteIdSelecionado]);

  // Todas as ordens de serviço (para lembrar a última OS do cliente ao
  // atender). Carregadas uma vez; lista local (SQLite), barato de filtrar.
  const [orcamentosTodos, setOrcamentosTodos] = useState<OrcamentoResumo[]>([]);
  useEffect(() => {
    void orcamentosApi.listar().then(setOrcamentosTodos).catch(() => undefined);
  }, []);

  const ultimaOsCliente = useMemo(() => {
    if (!clienteIdSelecionado) return null;
    const doCliente = orcamentosTodos
      .filter((o) => o.clienteId === clienteIdSelecionado && o.id !== idEdicao)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
    return doCliente[0] ?? null;
  }, [orcamentosTodos, clienteIdSelecionado, idEdicao]);

  const opcoesVeiculos = useMemo<OpcaoTypeahead[]>(() => {
    const mapa = new Map<string, VeiculoResumo>();
    for (const v of veiculosCliente) mapa.set(v.id, v);
    for (const v of veiculosBuscados) mapa.set(v.id, v);
    return Array.from(mapa.values()).map(toOpcaoVeiculo);
  }, [veiculosCliente, veiculosBuscados]);

  const mensagemVazioPlaca = carregandoVeiculos
    ? 'Carregando veículos…'
    : clienteIdSelecionado
      ? 'Nenhuma placa encontrada. Digite para cadastrar nova.'
      : 'Selecione um cliente acima para listar suas placas.';

  function veiculoPorId(id: string): VeiculoResumo | undefined {
    return (
      veiculosCliente.find((v) => v.id === id) ??
      veiculosBuscados.find((v) => v.id === id)
    );
  }

  function aoMudarPlaca(valor: ItemTypeaheadValor | null) {
    setAvisoCliente(null);
    setKmRegistrado('');
    if (!valor) {
      setPlacaSelecao(null);
      setDadosVeiculo(DADOS_VEICULO_VAZIO);
      return;
    }

    if (valor.tipo === 'existente') {
      const v = veiculoPorId(valor.opcao.id);
      if (v && clienteSelecao?.tipo === 'existente' && v.clienteId !== clienteSelecao.opcao.id) {
        // A placa é de outro cliente. NÃO troca o cliente: mantém o escolhido e
        // avisa que, ao salvar, será possível transferir o veículo (com
        // confirmação). Assim o histórico do dono anterior é preservado.
        const dono = clientes.find((cl) => cl.id === v.clienteId);
        setAvisoCliente(
          `Esta placa pertence a ${dono?.nome ?? 'outro cliente'}. Ao salvar, você poderá transferir o veículo para o cliente atual.`,
        );
      } else if (v && !clienteSelecao) {
        const c = clientes.find((cl) => cl.id === v.clienteId);
        if (c) setClienteSelecao({ tipo: 'existente', opcao: toOpcaoCliente(c) });
      }
      setPlacaSelecao(valor);
      setDadosVeiculo(DADOS_VEICULO_VAZIO);
      return;
    }

    const placa = normalizarPlaca(valor.descricao);
    setPlacaSelecao({ tipo: 'novo', descricao: placa });
  }

  /**
   * Campos lidos pelo celular (QR) ou pela câmera do próprio aparelho
   * entram pelo mesmo caminho do typeahead (`aoMudarPlaca`), então o aviso
   * de "placa de outro cliente" e a transferência no salvar continuam
   * valendo. Devolve o que foi aplicado, ou null se não deu.
   */
  function aplicarCampos(campos: CamposCapturaDTO) {
    if (ehEdicao) return null;
    if (!clienteSelecao) {
      // A placa só libera depois do cliente — e trocar o cliente zera a placa.
      setErro('Escolha o cliente antes de receber a placa do celular.');
      return null;
    }
    const r = aplicarCamposCapturados(
      {
        dadosVeiculo,
        kmRegistrado,
        semPlaca,
        veiculoSelecionadoId: placaSelecao?.tipo === 'existente' ? placaSelecao.opcao.id : null,
        placaNovaAtual: placaSelecao?.tipo === 'novo' ? placaSelecao.descricao : null,
        // Mesma lista que o typeahead oferece ao digitar a placa.
        veiculos: mesclarPorId(veiculosCliente, veiculosBuscados),
      },
      campos,
    );
    if (r.desmarcarSemPlaca) setSemPlaca(false);
    if (r.placa.tipo === 'existente') {
      const v = veiculoPorId(r.placa.veiculoId);
      if (v) aoMudarPlaca({ tipo: 'existente', opcao: toOpcaoVeiculo(v) });
    } else if (r.placa.tipo === 'novo') {
      aoMudarPlaca({ tipo: 'novo', descricao: r.placa.placa });
    }
    // `aoMudarPlaca` zera KM (e os dados, no existente); o resultado vem depois.
    setDadosVeiculo(r.placa.tipo === 'existente' ? DADOS_VEICULO_VAZIO : r.dadosVeiculo);
    setKmRegistrado(r.kmRegistrado);
    return r;
  }

  function receberCamposDoCelular(campos: CamposCapturaDTO) {
    if (!aplicarCampos(campos)) return;
    captura.confirmarCamposAplicados();
    setAvisoCaptura('Dados recebidos do celular');
    setModoQr(null);
  }

  /** Formulário descartado: encerra a captura para o celular parar de mandar fotos. */
  function cancelar() {
    if (!ehEdicao && captura.sessao) void captura.encerrar();
    aoCancelar();
  }

  /** Celular/tablet: a câmera do aparelho leu só a placa — busca o resto como no botão. */
  function receberPlacaDaCamera(placa: string) {
    const r = aplicarCampos({ placa });
    if (r?.placa.tipo === 'novo' && estaOnline) void consultarPlacaAtual(r.placa.placa);
  }

  useEffect(() => {
    if (placaSelecao?.tipo !== 'novo') return;
    const placa = normalizarPlaca(placaSelecao.descricao);
    if (placa.length !== 7) return;
    if (buscaPlacaTimer.current) clearTimeout(buscaPlacaTimer.current);
    buscaPlacaTimer.current = setTimeout(() => {
      void veiculosApi
        .buscarPorPlaca(placa)
        .then((res) => setVeiculosBuscados((atual) => mesclarPorId(atual, res)))
        .catch(() => undefined);
    }, 250);
    return () => {
      if (buscaPlacaTimer.current) clearTimeout(buscaPlacaTimer.current);
    };
  }, [placaSelecao]);

  function atualizarLinha(
    setLista: React.Dispatch<React.SetStateAction<LinhaItem[]>>,
    uid: string,
    patch: Partial<LinhaItem>,
  ) {
    setLista((atual) => atual.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));
  }

  function colaboradorResponsavelAtual(): ItemTypeaheadValor | null {
    return colaboradorResponsavel;
  }

  function mesmoColaborador(
    a: ItemTypeaheadValor | null,
    b: ItemTypeaheadValor | null,
  ): boolean {
    if (!a || !b) return false;
    return chaveSelecao(a) === chaveSelecao(b);
  }

  function aplicarResponsavelAItens(valorSelecionado: ItemTypeaheadValor) {
    const responsavelAnteriorAplicado = ultimoResponsavelAplicadoRef.current;
    const deveAplicar = (linha: LinhaItem) =>
      !linha.colaborador || mesmoColaborador(linha.colaborador, responsavelAnteriorAplicado);

    setPecas((atual) =>
      atual.map((l) => (deveAplicar(l) ? { ...l, colaborador: valorSelecionado } : l)),
    );
    setServicos((atual) =>
      atual.map((l) => (deveAplicar(l) ? { ...l, colaborador: valorSelecionado } : l)),
    );
    ultimoResponsavelAplicadoRef.current = valorSelecionado;
  }

  function alterarColaboradorResponsavel(valorSelecionado: ItemTypeaheadValor | null) {
    setColaboradorResponsavel(valorSelecionado);
    if (valorSelecionado?.tipo === 'existente') {
      setErro(null);
      aplicarResponsavelAItens(valorSelecionado);
    }
  }

  function confirmarNovoColaboradorResponsavel(descricao: string) {
    const nome = descricao.trim();
    if (nome.length < 2) {
      setErro('Nome do novo colaborador precisa ter ao menos 2 caracteres.');
      return;
    }
    const valor: ItemTypeaheadValor = { tipo: 'novo', descricao: nome };
    setErro(null);
    setColaboradorResponsavel(valor);
    aplicarResponsavelAItens(valor);
  }

  function removerLinha(
    setLista: React.Dispatch<React.SetStateAction<LinhaItem[]>>,
    uid: string,
  ) {
    setLista((atual) => atual.filter((l) => l.uid !== uid));
  }

  function chaveSelecao(s: ItemTypeaheadValor): string {
    return s.tipo === 'existente' ? `id:${s.opcao.id}` : `novo:${s.descricao.trim().toLowerCase()}`;
  }

  function jaUsadoEmOutraLinha(
    listaAtual: LinhaItem[],
    uidIgnorar: string,
    chave: string,
  ): boolean {
    return listaAtual.some(
      (l) => l.uid !== uidIgnorar && l.selecao && chaveSelecao(l.selecao) === chave,
    );
  }

  function aoSelecionarItem(linha: LinhaItem, valor: ItemTypeaheadValor | null) {
    const setLista = linha.tipo === 'Produto' ? setPecas : setServicos;
    const listaAtual = linha.tipo === 'Produto' ? pecas : servicos;
    const rotuloTipo = linha.tipo === 'Produto' ? 'Peça' : 'Serviço';

    if (valor) {
      const chave = chaveSelecao(valor);
      if (jaUsadoEmOutraLinha(listaAtual, linha.uid, chave)) {
        const desc = valor.tipo === 'existente' ? valor.opcao.rotulo : valor.descricao;
        setErro(`${rotuloTipo} "${desc}" já está em outra linha. Ajuste a quantidade na linha existente.`);
        atualizarLinha(setLista, linha.uid, { selecao: null, precoUnitario: 0 });
        return;
      }
    }

    setErro(null);
    // Peças sem estoque (0 ou negativo) são selecionáveis normalmente — o
    // estado do estoque é apenas informado na linha, sem bloquear ou limitar
    // a quantidade.
    if (valor?.tipo === 'existente') {
      atualizarLinha(setLista, linha.uid, {
        selecao: valor,
        precoUnitario: valor.opcao.precoUnitario ?? 0,
        quantidade: inteiroNaoNegativo(linha.quantidade),
      });
    } else {
      atualizarLinha(setLista, linha.uid, { selecao: valor });
    }
  }

  function validarSemDuplicatas(): string | null {
    for (const grupo of [
      { lista: pecas, rotulo: 'Peça' },
      { lista: servicos, rotulo: 'Serviço' },
    ]) {
      const vistas = new Map<string, string>();
      for (const l of grupo.lista) {
        if (!l.selecao) continue;
        const chave = chaveSelecao(l.selecao);
        const desc = l.selecao.tipo === 'existente' ? l.selecao.opcao.rotulo : l.selecao.descricao;
        if (vistas.has(chave)) {
          return `${grupo.rotulo} "${desc}" aparece em mais de uma linha.`;
        }
        vistas.set(chave, desc);
      }
    }
    return null;
  }


  const totaisPecas = useMemo(() => calcularTotais(pecas), [pecas]);
  const totaisServicos = useMemo(() => calcularTotais(servicos), [servicos]);

  const subtotalCombinado = arred(totaisPecas.comDesconto + totaisServicos.comDesconto);
  const valorDescontoGeral = arred(
    calcularDesconto(subtotalCombinado, descontoGeralTipo, descontoGeralValor),
  );
  const totalFinal = arred(subtotalCombinado - valorDescontoGeral);

  const placaIsNova = placaSelecao?.tipo === 'novo';
  // Fluxo guiado: a placa/veículo só liberam depois que o cliente é escolhido.
  // Em edição o cliente já existe (read-only), então não há gate a aplicar.
  const clientePreenchido = ehEdicao || Boolean(clienteSelecao);

  // Alvo do botão "completar cadastro": o cliente cadastrado (com os dados
  // atuais da lista) ou o nome digitado que ainda não virou cadastro.
  const clienteParaCompletar = useMemo<ClienteParaCompletar | null>(() => {
    if (clienteSelecao?.tipo === 'novo') {
      const nome = clienteSelecao.descricao.trim();
      return nome ? { tipo: 'novo', nome } : null;
    }
    if (clienteSelecao?.tipo === 'existente') {
      const cliente = clientes.find((c) => c.id === clienteSelecao.opcao.id);
      return cliente ? { tipo: 'existente', cliente } : null;
    }
    return null;
  }, [clienteSelecao, clientes]);

  const dicaCliente = ehEdicao
    ? 'Não pode ser trocado. O ícone ao lado abre o cadastro.'
    : clienteSelecao?.tipo === 'novo'
      ? 'Será cadastrado ao salvar. Telefone e documento: ícone ao lado.'
      : cadastroClienteIncompleto(clienteParaCompletar)
        ? 'Cadastro incompleto — complete pelo ícone ao lado.'
        : 'Digite para buscar ou cadastrar novo.';

  /**
   * Cadastro gravado pelo modal: recarrega a lista e reaponta a seleção — o
   * nome pode ter mudado, e o cliente digitado agora é cadastrado (com id).
   */
  async function aoSalvarCadastroCliente(dados: AtualizarClienteRequest, clienteId: string) {
    // Fecha antes de recarregar: o cadastro já está salvo, e uma falha na
    // lista não pode deixar o modal aberto (outro "Salvar" duplicaria o novo).
    setCompletandoCliente(false);
    const cs = await clientesApi.listar().catch(() => null);
    if (cs) {
      setClientes(cs);
      setOpcoesClientes(cs.map(toOpcaoCliente));
    }
    const salvo = cs?.find((c) => c.id === clienteId);
    setClienteSelecao({
      tipo: 'existente',
      opcao: salvo
        ? toOpcaoCliente(salvo)
        : { id: clienteId, rotulo: dados.nome, busca: dados.nome },
    });
  }
  // Colaborador responsável é obrigatório ao criar e ao editar O.S. que
  // ainda não chegou a uma etapa terminal (assim as antigas abertas sem
  // responsável passam a ser preenchidas). Fechada/paga ou cancelada fica
  // como está — o backend nem aceita editar essas.
  const exigeResponsavel = !ehEdicao || status === null || !ehTerminal(status);

  async function resolverColaborador(
    valor: ItemTypeaheadValor | null,
    colaboradoresCriados: Map<string, string>,
  ): Promise<string | null> {
    if (!valor) return null;
    if (valor.tipo === 'existente') return valor.opcao.id;

    const nome = valor.descricao.trim();
    if (nome.length < 2) {
      throw new Error('Nome do novo colaborador precisa ter ao menos 2 caracteres.');
    }
    const chave = normalizarChave(nome);
    const existente = colaboradoresCriados.get(chave);
    if (existente) return existente;
    const resp = await colaboradoresApi.criar({ nome, ativo: true });
    colaboradoresCriados.set(chave, resp.id);
    return resp.id;
  }

  async function resolverColaboradoresParaSalvar(): Promise<{
    colaboradorResponsavelId: string | null;
    colaboradoresPorLinha: Map<string, string | null>;
  }> {
    const colaboradoresCriados = new Map<string, string>();
    const colaboradorResponsavelId = await resolverColaborador(
      colaboradorResponsavel,
      colaboradoresCriados,
    );
    const colaboradoresPorLinha = new Map<string, string | null>();
    for (const linha of [...pecas, ...servicos]) {
      const colaboradorDaLinha = linha.colaborador ?? colaboradorResponsavel;
      colaboradoresPorLinha.set(
        linha.uid,
        await resolverColaborador(colaboradorDaLinha, colaboradoresCriados),
      );
    }
    return { colaboradorResponsavelId, colaboradoresPorLinha };
  }

  // Altera o status na edição direto via API (independente do "Salvar
  // alterações"). A transição pode aplicar baixa/estorno de estoque no backend.
  async function aoMudarStatusEdicao(novo: StatusOrcamento) {
    if (!idEdicao || novo === status) return;
    setSalvandoStatus(true);
    setErro(null);
    try {
      await orcamentosApi.alterarStatus(idEdicao, novo);
      setStatus(novo);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao alterar o status.');
    } finally {
      setSalvandoStatus(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);

    const erroDup = validarSemDuplicatas();
    if (erroDup) {
      setErro(erroDup);
      return;
    }

    if (ehEdicao && idEdicao) {
      if (exigeResponsavel && !colaboradorResponsavel) {
        setErro('Informe o colaborador responsável.');
        return;
      }
      const descontoGeral: DescontoEntrada | null =
        descontoGeralValor > 0 ? { tipo: descontoGeralTipo, valor: descontoGeralValor } : null;
      setSalvando(true);
      try {
        const { colaboradorResponsavelId, colaboradoresPorLinha } =
          await resolverColaboradoresParaSalvar();
        const itens = montarItens([...pecas, ...servicos], colaboradoresPorLinha);
        await orcamentosApi.atualizar(idEdicao, {
          prazoEntrega: new Date(prazoEntrega).toISOString(),
          observacoes: observacoes.trim() === '' ? null : observacoes,
          itens,
          descontoGeral,
          colaboradorResponsavelId,
        });
        limparRascunhoVenda(idEdicao);
        await aoSalvar();
      } catch (e) {
        setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
      } finally {
        setSalvando(false);
      }
      return;
    }

    if (!clienteSelecao) {
      setErro('Selecione um cliente ou digite o nome para cadastrar.');
      return;
    }
    if (clienteSelecao.tipo === 'novo' && clienteSelecao.descricao.trim().length < 2) {
      setErro('Nome do novo cliente precisa ter ao menos 2 caracteres.');
      return;
    }
    if (!semPlaca && !placaSelecao) {
      setErro('Selecione uma placa, digite uma nova ou marque "Sem placa".');
      return;
    }
    if (!colaboradorResponsavel) {
      setErro('Informe o colaborador responsável.');
      return;
    }

    let placaFinal: string | null = null;
    let veiculoIdFinal: string | null = null;
    let dadosNovoVeiculo: DadosVeiculoNovo | null = null;

    if (semPlaca) {
      // Sem placa o veículo continua sendo cadastrado — só que identificado
      // por marca/modelo/ano. Ano e motor ficam opcionais: nem toda máquina
      // ou frota interna tem essa informação à mão.
      placaFinal = null;
      const marca = dadosVeiculo.marca.trim();
      const modelo = dadosVeiculo.modelo.trim();
      if (!marca || !modelo) {
        setErro('Sem placa, marca e modelo do veículo são obrigatórios.');
        return;
      }
      if (dadosVeiculo.ano !== '') {
        const anoNum = Number(dadosVeiculo.ano);
        if (!Number.isInteger(anoNum) || anoNum < 1900 || anoNum > new Date().getFullYear() + 1) {
          setErro('Ano do veículo inválido.');
          return;
        }
      }
      if (dadosVeiculo.km !== '') {
        const kmNum = Number(dadosVeiculo.km);
        if (!Number.isInteger(kmNum) || kmNum < 0 || kmNum > 9_999_999) {
          setErro('KM do veículo inválido.');
          return;
        }
      }
      dadosNovoVeiculo = {
        marca,
        modelo,
        ano: dadosVeiculo.ano,
        motor: dadosVeiculo.motor.trim(),
        km: dadosVeiculo.km,
      };
    } else {
      const ps = placaSelecao!;
      if (ps.tipo === 'existente') {
        const v = veiculoPorId(ps.opcao.id);
        if (!v) {
          setErro('Veículo selecionado não encontrado. Tente novamente.');
          return;
        }
        placaFinal = v.placa;
        veiculoIdFinal = v.id;
      } else {
        placaFinal = normalizarPlaca(ps.descricao);
        if (placaFinal.length !== 7) {
          setErro('Placa precisa ter 7 caracteres.');
          return;
        }
        const marca = dadosVeiculo.marca.trim();
        const modelo = dadosVeiculo.modelo.trim();
        const motor = dadosVeiculo.motor.trim();
        const anoNum = Number(dadosVeiculo.ano);
        if (!marca || !modelo) {
          setErro('Para placa nova, marca e modelo são obrigatórios.');
          return;
        }
        if (
          !Number.isInteger(anoNum) ||
          anoNum < 1900 ||
          anoNum > new Date().getFullYear() + 1
        ) {
          setErro('Ano do veículo inválido.');
          return;
        }
        if (dadosVeiculo.km !== '') {
          const kmNum = Number(dadosVeiculo.km);
          if (!Number.isInteger(kmNum) || kmNum < 0 || kmNum > 9_999_999) {
            setErro('KM do veículo inválido.');
            return;
          }
        }
        dadosNovoVeiculo = { marca, modelo, ano: String(anoNum), motor, km: dadosVeiculo.km };
      }
    }

    const descontoGeral: DescontoEntrada | null =
      descontoGeralValor > 0 ? { tipo: descontoGeralTipo, valor: descontoGeralValor } : null;

    setSalvando(true);
    try {
      const { colaboradorResponsavelId, colaboradoresPorLinha } =
        await resolverColaboradoresParaSalvar();
      const itens = montarItens([...pecas, ...servicos], colaboradoresPorLinha);
      let clienteIdParaVeiculo: string | null =
        clienteSelecao.tipo === 'existente' ? clienteSelecao.opcao.id : null;
      let clienteCriado = false;

      // Precisamos do cliente concreto sempre que houver veículo a vincular —
      // tanto para criar um veículo novo quanto para transferir um existente.
      const precisaResolverVeiculo =
        (Boolean(placaFinal) || semPlaca) &&
        (Boolean(veiculoIdFinal) || dadosNovoVeiculo !== null);
      if (clienteIdParaVeiculo === null && precisaResolverVeiculo && clienteSelecao.tipo === 'novo') {
        const respCliente = await clientesApi.criar({ nome: clienteSelecao.descricao.trim() });
        clienteIdParaVeiculo = respCliente.id;
        clienteCriado = true;
      }

      const nomeClienteAtual =
        clienteSelecao.tipo === 'existente'
          ? clienteSelecao.opcao.rotulo
          : clienteSelecao.descricao.trim();

      // Sem placa não há chave para procurar duplicata nem transferir dono —
      // cadastra direto e vincula à OS. Dois "Fiat Uno 2015" do mesmo cliente
      // são dois carros distintos, e é isso que queremos.
      if (semPlaca && dadosNovoVeiculo && clienteIdParaVeiculo) {
        const resp = await veiculosApi.cadastrar({
          clienteId: clienteIdParaVeiculo,
          placa: null,
          marca: dadosNovoVeiculo.marca,
          modelo: dadosNovoVeiculo.modelo,
          ano: dadosNovoVeiculo.ano !== '' ? Number(dadosNovoVeiculo.ano) : null,
          motor: dadosNovoVeiculo.motor,
          // Sem KM aqui de propósito: a leitura é registrada pela própria OS
          // logo abaixo, com vínculo à ordem. Mandar nos dois lugares criaria
          // duas linhas no histórico do veículo para a mesma visita.
          kmAtual: null,
        });
        veiculoIdFinal = resp.id;
      }

      // Resolve o veículo garantindo PLACA ÚNICA (nunca duplica). Se a placa já
      // pertence a outro cliente, confirma a transferência; os orçamentos
      // antigos seguem ligados ao dono anterior (histórico preservado).
      if (placaFinal && clienteIdParaVeiculo) {
        // (a) Veículo existente escolhido na lista de placas. Só entra aqui
        // quando tem placa — `placaFinal` guarda o filtro deste bloco.
        if (veiculoIdFinal) {
          const v = veiculoPorId(veiculoIdFinal);
          if (v && v.placa && v.clienteId !== clienteIdParaVeiculo) {
            const donoAnterior =
              clientes.find((cl) => cl.id === v.clienteId)?.nome ?? 'outro cliente';
            const ok = await dialogos.confirmar({
              titulo: 'Transferir veículo?',
              mensagem: `A placa ${v.placa} pertence a ${donoAnterior}. Deseja transferir o veículo para ${nomeClienteAtual}?\n\nAs ordens de serviço anteriores continuarão no histórico de ${donoAnterior}.`,
              textoConfirmar: 'Transferir',
            });
            if (!ok) return;
            const resp = await veiculosApi.cadastrarPorPlaca({
              clienteId: clienteIdParaVeiculo,
              placa: v.placa,
            });
            veiculoIdFinal = resp.id;
          }
        }
        // (b) Placa digitada como "nova": pode já existir no banco.
        else if (dadosNovoVeiculo) {
          const existentes = await veiculosApi.buscarPorPlaca(placaFinal);
          const existente = existentes[0];
          if (existente) {
            if (existente.clienteId !== clienteIdParaVeiculo) {
              const donoAnterior =
                clientes.find((cl) => cl.id === existente.clienteId)?.nome ?? 'outro cliente';
              const ok = await dialogos.confirmar({
                titulo: 'Veículo já cadastrado',
                mensagem: `Já existe um veículo com a placa ${placaFinal} (de ${donoAnterior}). Deseja transferi-lo para ${nomeClienteAtual}?\n\nAs ordens de serviço anteriores continuarão no histórico de ${donoAnterior}.`,
                textoConfirmar: 'Transferir',
              });
              if (!ok) return;
              const resp = await veiculosApi.cadastrarPorPlaca({
                clienteId: clienteIdParaVeiculo,
                placa: placaFinal,
              });
              veiculoIdFinal = resp.id;
            } else {
              veiculoIdFinal = existente.id;
            }
          } else {
            const resp = await veiculosApi.cadastrar({
              clienteId: clienteIdParaVeiculo,
              placa: placaFinal,
              marca: dadosNovoVeiculo.marca,
              modelo: dadosNovoVeiculo.modelo,
              ano: Number(dadosNovoVeiculo.ano),
              motor: dadosNovoVeiculo.motor,
              // Idem: quem registra o KM é a OS, com vínculo à ordem.
              kmAtual: null,
            });
            veiculoIdFinal = resp.id;
          }
        }
      }

      // O KM informado na abertura entra na OS venha de onde vier: do card
      // "KM atual do veículo" (veículo já cadastrado, escolhido pela placa) ou
      // do campo KM do formulário de veículo novo. Antes só o primeiro caminho
      // registrava, e a OS de um veículo recém-cadastrado saía sem KM.
      const kmDigitado = dadosNovoVeiculo ? dadosNovoVeiculo.km : kmRegistrado;
      const kmAtualNum = kmDigitado.trim() === '' ? null : Number(kmDigitado);
      const kmValidoParaEnvio =
        kmAtualNum != null && Number.isInteger(kmAtualNum) && kmAtualNum >= 0 ? kmAtualNum : null;

      const usouClienteJaCriado = clienteCriado;

      const criado = await orcamentosApi.criar({
        clienteId: usouClienteJaCriado
          ? clienteIdParaVeiculo
          : clienteSelecao.tipo === 'existente'
            ? clienteSelecao.opcao.id
            : null,
        clienteNovo:
          !usouClienteJaCriado && clienteSelecao.tipo === 'novo'
            ? { nome: clienteSelecao.descricao.trim() }
            : null,
        placa: placaFinal,
        veiculoId: veiculoIdFinal,
        prazoEntrega: new Date(prazoEntrega).toISOString(),
        observacoes: observacoes.trim() === '' ? null : observacoes,
        itens,
        descontoGeral,
        colaboradorResponsavelId,
        kmRegistrado: kmValidoParaEnvio,
      });
      limparRascunhoVenda(idEdicao);
      // Fotos tiradas pelo celular antes de salvar passam a ser desta O.S.
      // Falha não desfaz nada: o vínculo fica na fila deste aparelho e é
      // refeito quando a O.S. for aberta com internet.
      const sessoesCaptura = captura.idsSessoes();
      if (criado?.id && sessoesCaptura.length > 0) {
        const vinculo = await vincularSessoesAoOrcamento(sessoesCaptura, criado.id);
        if (vinculo.erro && captura.fotos.length > 0) {
          // Diálogo global (não Toast): a tela fecha logo abaixo, no aoSalvar.
          void dialogos.alertar({
            titulo: 'Fotos ainda não vinculadas',
            mensagem: `A ordem de serviço foi salva, mas as fotos do celular não puderam ser vinculadas agora: ${vinculo.erro}\n\nElas ficam guardadas e entram na ordem de serviço quando você abri-la com internet.`,
          });
        }
      }
      await aoSalvar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo={ehEdicao ? 'Editar ordem de serviço' : 'Nova ordem de serviço'}
        descricao={
          ehEdicao
            ? 'Cliente e placa não podem ser alterados.'
            : 'Informe cliente e placa. Peças e serviços são opcionais.'
        }
        acoes={
          <>
            <Button onClick={cancelar} disabled={salvando}>
              Cancelar
            </Button>
            <Button
              variante="primary"
              onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
              disabled={salvando}
            >
              {salvando ? 'Salvando…' : ehEdicao ? 'Salvar alterações' : 'Salvar ordem de serviço'}
            </Button>
          </>
        }
      />

      <Toast mensagem={erro} tipo="erro" onFechar={() => setErro(null)} />
      <Toast mensagem={avisoCaptura} tipo="sucesso" onFechar={fecharAvisoCaptura} />

      <form className="form-grid" onSubmit={handleSubmit}>

        {ehEdicao && status && (
          <div className="card">
            <div className="card__header">
              <span className="card__title">Status da ordem de serviço</span>
            </div>
            <div className="card__body">
              <div className="field" style={{ maxWidth: 380 }}>
                <label className="field__label" htmlFor="orcamento-status-edicao">
                  Situação atual
                </label>
                <select
                  id="orcamento-status-edicao"
                  className="select"
                  value={status}
                  disabled={salvandoStatus || (status !== null && ehTerminal(status))}
                  onChange={(e) => void aoMudarStatusEdicao(e.target.value as StatusOrcamento)}
                >
                  {etapas.map((et) => (
                    <option key={et.id} value={et.id}>
                      {et.rotulo}
                    </option>
                  ))}
                </select>
                <span className="field__hint">
                  {salvandoStatus
                    ? 'Salvando…'
                    : status !== null && ehTerminal(status)
                      ? 'Etapa final — a ordem de serviço fica somente para consulta.'
                      : 'Aplicado na hora; pode dar baixa/estorno de estoque automaticamente.'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card__header">
            <span className="card__title">Cliente</span>
          </div>
          <div className="card__body orcamento-cabecalho">
            {ultimaOsCliente && (
              <div
                className="info-banner"
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'baseline',
                }}
              >
                <strong>Última OS deste cliente:</strong>
                <span>
                  {ultimaOsCliente.numero} · {formatarDataCurta(ultimaOsCliente.data)} (
                  {diasAtras(ultimaOsCliente.data)}) ·{' '}
                  {rotuloDe(ultimaOsCliente.status)} ·{' '}
                  {formatadorMoeda.format(ultimaOsCliente.total)}
                  {ultimaOsCliente.placa ? ` · ${ultimaOsCliente.placa}` : ''}
                </span>
              </div>
            )}
            {/*
              Ordem pensada para o celular (uma coluna): cliente → placa →
              veículo → prazo → colaborador → observações. O veículo fica
              colado na placa, que é de onde ele vem; no desktop o grid só
              distribui os mesmos campos em colunas (ver `.orcamento-cabecalho`).
            */}
            <div className="orcamento-cabecalho-grid">
              <div className="field orcamento-cabecalho__cliente">
                <span className="field__label">
                  Cliente
                  {!ehEdicao && (
                    <>
                      <MarcaObrigatorio />
                      <span className="sr-only"> (obrigatório)</span>
                    </>
                  )}
                </span>
                <div className="campo-com-acao">
                  <div className="campo-com-acao__campo">
                    {ehEdicao ? (
                      <input
                        className="input"
                        aria-label="Cliente"
                        value={
                          clienteSelecao?.tipo === 'existente'
                            ? clienteSelecao.opcao.rotulo
                            : ''
                        }
                        readOnly
                        disabled
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <ItemTypeahead
                        ariaLabel="Cliente"
                        opcoes={opcoesClientes}
                        valor={clienteSelecao}
                        onChange={(v) => {
                          setClienteSelecao(v);
                          setPlacaSelecao(null);
                          setDadosVeiculo(DADOS_VEICULO_VAZIO);
                          setAvisoCliente(null);
                          setSemPlaca(false);
                        }}
                        placeholder="Buscar ou cadastrar cliente…"
                      />
                    )}
                  </div>
                  <BotaoCompletarCliente
                    alvo={clienteParaCompletar}
                    aoAbrir={() => setCompletandoCliente(true)}
                  />
                </div>
                <span className="field__hint">{dicaCliente}</span>
              </div>

              <div className="field orcamento-cabecalho__placa">
                <span className="field__label field__label--com-acao">
                  <span>
                    Placa
                    {!ehEdicao && !semPlaca && (
                      <>
                        <MarcaObrigatorio />
                        <span className="sr-only"> (obrigatório)</span>
                      </>
                    )}
                  </span>
                  {!ehEdicao && clienteSelecao && (
                    <label className="checkbox checkbox--compact">
                      <input
                        type="checkbox"
                        checked={semPlaca}
                        onChange={(e) => {
                          const marcado = e.target.checked;
                          setSemPlaca(marcado);
                          if (marcado) {
                            setPlacaSelecao(null);
                            setDadosVeiculo(DADOS_VEICULO_VAZIO);
                          }
                        }}
                      />
                      <span>Sem placa</span>
                    </label>
                  )}
                </span>
                {ehEdicao ? (
                  <input
                    className="input"
                    value={placaExibicao || '— sem placa —'}
                    readOnly
                    disabled
                    style={{ textTransform: 'uppercase' }}
                  />
                ) : (
                  // Câmera colada ao campo (não no rótulo, que já tem o
                  // "Sem placa"): em 360px o campo encolhe e o botão fica.
                  <div className="captura-placa-linha">
                    <div className="captura-placa-linha__campo">
                      {!clientePreenchido ? (
                        <input
                          className="input input--bloqueado"
                          value=""
                          placeholder="Selecione o cliente primeiro"
                          readOnly
                          disabled
                          aria-disabled="true"
                          style={{ width: '100%' }}
                        />
                      ) : semPlaca ? (
                        <input
                          className="input"
                          value="— sem placa —"
                          readOnly
                          disabled
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <ItemTypeahead
                          ariaLabel="Placa"
                          opcoes={opcoesVeiculos}
                          valor={placaSelecao}
                          onChange={aoMudarPlaca}
                          placeholder="ABC1D23"
                          mensagemVazio={mensagemVazioPlaca}
                        />
                      )}
                    </div>
                    <BotaoCameraPlaca
                      desabilitado={!clientePreenchido}
                      aoAcionar={() => {
                        setStatusLeituraPlaca(null);
                        if (semPlaca) setSemPlaca(false);
                      }}
                      aoPedirQr={() => setModoQr('placa')}
                      aoLerPlaca={({ placa }) => receberPlacaDaCamera(placa)}
                      aoMudarStatus={setStatusLeituraPlaca}
                    />
                  </div>
                )}
                {!ehEdicao && statusLeituraPlaca ? (
                  <span
                    className={`field__hint${
                      statusLeituraPlaca.tipo === 'erro'
                        ? ' captura-hint--erro'
                        : statusLeituraPlaca.tipo === 'aviso'
                          ? ' captura-hint--aviso'
                          : ''
                    }`}
                    role={statusLeituraPlaca.tipo === 'erro' ? 'alert' : 'status'}
                  >
                    {statusLeituraPlaca.mensagem}
                  </span>
                ) : (
                  <span className="field__hint">
                    {ehEdicao
                      ? 'Não pode ser alterada.'
                      : !clientePreenchido
                        ? 'Disponível após escolher o cliente.'
                        : semPlaca
                          ? 'Preencha os dados do veículo abaixo — ele será identificado por marca, modelo e ano.'
                          : 'Selecione uma placa existente, digite uma nova ou use a câmera.'}
                  </span>
                )}
              </div>

              {!ehEdicao && (semPlaca || placaIsNova) && (
                <div
                  role="group"
                  aria-labelledby={idTituloVeiculo}
                  className="veiculo-painel orcamento-cabecalho__cheio"
                >
                  <div className="veiculo-painel__cabecalho">
                    <span id={idTituloVeiculo} className="veiculo-painel__titulo">
                      {semPlaca ? 'Veículo sem placa' : 'Veículo novo'}
                    </span>
                    {/* Consulta por placa só faz sentido quando existe placa. */}
                    {!semPlaca && (!estaOnline || avisoConsultaPlaca) && (
                      <span
                        className={`veiculo-painel__status${
                          estaOnline && avisoConsultaPlaca?.tipo === 'erro'
                            ? ' veiculo-painel__status--erro'
                            : ''
                        }`}
                        role="status"
                      >
                        {!estaOnline
                          ? 'Sem internet — preencha à mão.'
                          : avisoConsultaPlaca?.mensagem}
                      </span>
                    )}
                    {!semPlaca && (
                      <Button
                        className="veiculo-painel__buscar"
                        onClick={() => void consultarPlacaAtual()}
                        disabled={consultandoPlaca || !estaOnline}
                        aria-label="Buscar dados da placa no banco Oficina Ágil"
                        title={
                          !estaOnline
                            ? 'Consulta de placa requer conexão com a internet.'
                            : undefined
                        }
                      >
                        <IconSearch aria-hidden="true" width={16} height={16} />
                        {consultandoPlaca ? (
                          'Consultando…'
                        ) : (
                          <span>
                            Buscar dados
                            <span className="veiculo-painel__texto-longo"> da placa</span>
                          </span>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="veiculo-painel__grade">
                    <Field rotulo="Marca" obrigatorio>
                      {(id) => (
                        <input
                          id={id}
                          className="input"
                          value={dadosVeiculo.marca}
                          onChange={(e) =>
                            setDadosVeiculo((d) => ({ ...d, marca: e.target.value }))
                          }
                          maxLength={60}
                          required
                        />
                      )}
                    </Field>
                    <Field rotulo="Modelo" obrigatorio>
                      {(id) => (
                        <input
                          id={id}
                          className="input"
                          value={dadosVeiculo.modelo}
                          onChange={(e) =>
                            setDadosVeiculo((d) => ({ ...d, modelo: e.target.value }))
                          }
                          maxLength={80}
                          required
                        />
                      )}
                    </Field>
                    {/* Sem placa o ano é opcional — a dica da Placa já explica
                        que marca, modelo e ano identificam o veículo. */}
                    <Field rotulo="Ano" obrigatorio={!semPlaca}>
                      {(id) => (
                        <input
                          id={id}
                          className="input"
                          value={dadosVeiculo.ano}
                          onChange={(e) =>
                            setDadosVeiculo((d) => ({
                              ...d,
                              ano: e.target.value.replace(/\D/g, '').slice(0, 4),
                            }))
                          }
                          inputMode="numeric"
                          placeholder={String(new Date().getFullYear())}
                          required={!semPlaca}
                        />
                      )}
                    </Field>
                    <Field rotulo="Motor">
                      {(id) => (
                        <input
                          id={id}
                          className="input"
                          value={dadosVeiculo.motor}
                          onChange={(e) =>
                            setDadosVeiculo((d) => ({ ...d, motor: e.target.value }))
                          }
                          maxLength={60}
                          placeholder="Ex.: 1.6"
                        />
                      )}
                    </Field>
                    <Field rotulo="KM atual">
                      {(id) => (
                        <input
                          id={id}
                          className="input"
                          value={dadosVeiculo.km}
                          onChange={(e) =>
                            setDadosVeiculo((d) => ({
                              ...d,
                              km: e.target.value.replace(/\D/g, '').slice(0, 7),
                            }))
                          }
                          inputMode="numeric"
                          placeholder="85000"
                        />
                      )}
                    </Field>
                  </div>
                </div>
              )}

              {!ehEdicao && !semPlaca && placaSelecao?.tipo === 'existente' && (() => {
                const v = veiculoPorId(placaSelecao.opcao.id);
                if (!v) return null;
                const kmAtualLabel =
                  v.kmAtual != null
                    ? `${v.kmAtual.toLocaleString('pt-BR')} km`
                    : '— sem registro —';
                return (
                  <div
                    role="group"
                    aria-label="Quilometragem do veículo"
                    className="veiculo-painel orcamento-cabecalho__cheio"
                  >
                    {avisoCliente && (
                      <p className="veiculo-painel__aviso" role="status">
                        {avisoCliente}
                      </p>
                    )}
                    <div className="veiculo-painel__grade">
                      <div className="veiculo-painel__km-atual">
                        <span className="field__label">KM atual do veículo</span>
                        <strong>{kmAtualLabel}</strong>
                      </div>
                      <Field rotulo="KM no atendimento">
                        {(id) => (
                          <input
                            id={id}
                            className="input"
                            value={kmRegistrado}
                            onChange={(e) =>
                              setKmRegistrado(e.target.value.replace(/\D/g, '').slice(0, 7))
                            }
                            inputMode="numeric"
                            placeholder={
                              v.kmAtual != null
                                ? `Ex.: ${(v.kmAtual + 1000).toLocaleString('pt-BR')}`
                                : 'Ex.: 85000'
                            }
                          />
                        )}
                      </Field>
                      <span className="veiculo-painel__nota">
                        Opcional. Fica no histórico de KM do veículo.
                      </span>
                    </div>
                  </div>
                );
              })()}

              <Field rotulo="Prazo de entrega" obrigatorio className="orcamento-cabecalho__prazo">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="date"
                    value={prazoEntrega}
                    onChange={(e) => setPrazoEntrega(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field
                rotulo="Colaborador responsável"
                obrigatorio={exigeResponsavel}
                dica="Será aplicado automaticamente aos itens sem colaborador."
                className="orcamento-cabecalho__colaborador"
              >
                {() => (
                  <ItemTypeahead
                    ariaLabel="Colaborador responsável"
                    opcoes={opcoesColaboradores}
                    valor={colaboradorResponsavel}
                    onChange={alterarColaboradorResponsavel}
                    onConfirmarNovo={confirmarNovoColaboradorResponsavel}
                    placeholder="Buscar ou cadastrar colaborador..."
                    mensagemVazio="Digite o nome para cadastrar um novo colaborador."
                  />
                )}
              </Field>

              <Field rotulo="Observações" dica="Opcional." className="orcamento-cabecalho__observacoes">
                {(id) => (
                  <textarea
                    id={id}
                    className="input"
                    rows={2}
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>

        {ehEdicao && idEdicao ? (
          <GaleriaFotosOS
            orcamentoId={idEdicao}
            numero={numeroEdicao}
            placa={placaExibicao || null}
          />
        ) : (
          <FaixaFotosCaptura
            fotos={captura.fotos}
            aoTirarFotos={() => setModoQr('fotos')}
            desabilitado={!estaOnline}
            motivo="Requer internet"
          />
        )}

        <SecaoItens
          titulo="Peças"
          tipo="Produto"
          linhas={pecas}
          opcoes={opcoesProdutos}
          colaboradores={opcoesColaboradores}
          erro={
            erro && (erro.toLowerCase().includes('peça') || erro.toLowerCase().includes('estoque'))
              ? erro
              : null
          }
          onSelecionar={aoSelecionarItem}
          onAtualizar={(uid, patch) => atualizarLinha(setPecas, uid, patch)}
          onRemover={(uid) => removerLinha(setPecas, uid)}
          onAdicionar={() => setPecas((a) => [...a, novaLinha('Produto', colaboradorResponsavelAtual())])}
        />

        <SecaoItens
          titulo="Serviços"
          tipo="Servico"
          linhas={servicos}
          opcoes={opcoesServicos}
          colaboradores={opcoesColaboradores}
          erro={erro && erro.toLowerCase().includes('serviço') ? erro : null}
          onSelecionar={aoSelecionarItem}
          onAtualizar={(uid, patch) => atualizarLinha(setServicos, uid, patch)}
          onRemover={(uid) => removerLinha(setServicos, uid)}
          onAdicionar={() => setServicos((a) => [...a, novaLinha('Servico', colaboradorResponsavelAtual())])}
        />

        <div className="totais-cards-grid">
          <CardTotalSecao titulo="Total de peças" totais={totaisPecas} />
          <CardTotalSecao titulo="Total de serviços" totais={totaisServicos} />

          <div className="card totais-card-geral">
            <div className="card__header">
              <span className="card__title">Total de peças e serviços</span>
            </div>
            <div className="card__body totais-rodape">
              <div className="totais-rodape__linha">
                <span>Total de peças</span>
                <strong>{formatadorMoeda.format(totaisPecas.comDesconto)}</strong>
              </div>
              <div className="totais-rodape__linha">
                <span>Total de serviços</span>
                <strong>{formatadorMoeda.format(totaisServicos.comDesconto)}</strong>
              </div>
              <div className="totais-rodape__linha">
                <span>Subtotal</span>
                <strong>{formatadorMoeda.format(subtotalCombinado)}</strong>
              </div>
              <div className="totais-rodape__linha totais-rodape__desconto-geral">
                <span>Desconto geral</span>
                <select
                  aria-label="Tipo do desconto geral"
                  className="input"
                  value={descontoGeralTipo}
                  onChange={(e) => setDescontoGeralTipo(e.target.value as TipoDesconto)}
                >
                  <option value="Percentual">%</option>
                  <option value="Valor">R$</option>
                </select>
                <InputMonetario
                  ariaLabel="Valor do desconto geral"
                  valor={descontoGeralValor}
                  onChange={setDescontoGeralValor}
                />
                <strong>− {formatadorMoeda.format(valorDescontoGeral)}</strong>
              </div>
              <div className="totais-rodape__linha totais-rodape__total">
                <span>Total final</span>
                <strong>{formatadorMoeda.format(totalFinal)}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="form-rodape-acoes">
          <Button onClick={cancelar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : ehEdicao ? 'Salvar alterações' : 'Salvar ordem de serviço'}
          </Button>
        </div>
      </form>

      {/* Fora do <form> da O.S.: o modal tem o próprio formulário. */}
      {completandoCliente && (
        <ClienteFormModal
          modo={
            clienteParaCompletar?.tipo === 'existente'
              ? { tipo: 'editar', cliente: clienteParaCompletar.cliente }
              : { tipo: 'novo', nome: clienteParaCompletar?.nome }
          }
          somenteDados
          titulo={
            clienteParaCompletar?.tipo === 'existente'
              ? 'Cadastro do cliente'
              : 'Cadastrar cliente'
          }
          aoFechar={() => setCompletandoCliente(false)}
          aoSalvar={aoSalvarCadastroCliente}
        />
      )}

      {!ehEdicao && (
        <ModalCapturaCelular
          aberto={modoQr !== null}
          modo={modoQr ?? 'placa'}
          captura={captura}
          parametros={{
            orcamentoId: null,
            numero: null,
            placa:
              placaSelecao?.tipo === 'novo'
                ? normalizarPlaca(placaSelecao.descricao) || null
                : placaSelecao?.tipo === 'existente'
                  ? veiculoPorId(placaSelecao.opcao.id)?.placa ?? null
                  : null,
          }}
          onFechar={() => setModoQr(null)}
        />
      )}
    </section>
  );
}

type TotaisSecao = { bruto: number; desconto: number; comDesconto: number };

function mesclarPorId(atuais: VeiculoResumo[], novos: VeiculoResumo[]): VeiculoResumo[] {
  const mapa = new Map<string, VeiculoResumo>();
  for (const v of atuais) mapa.set(v.id, v);
  for (const v of novos) mapa.set(v.id, v);
  return Array.from(mapa.values());
}

function calcularTotais(linhas: LinhaItem[]): TotaisSecao {
  let bruto = 0;
  let desconto = 0;
  for (const l of linhas) {
    if (!l.selecao || l.quantidade <= 0) continue;
    const sub = arred(l.quantidade * l.precoUnitario);
    const desc = arred(calcularDesconto(sub, l.descontoTipo, l.descontoValor));
    bruto += sub;
    desconto += desc;
  }
  return {
    bruto: arred(bruto),
    desconto: arred(desconto),
    comDesconto: arred(bruto - desconto),
  };
}

function montarItens(
  linhas: LinhaItem[],
  colaboradoresPorLinha: Map<string, string | null>,
): ItemOrcamentoEntrada[] {
  const itens: ItemOrcamentoEntrada[] = [];
  for (const l of linhas) {
    if (!l.selecao) continue;
    if (l.quantidade <= 0 || l.precoUnitario < 0) continue;
    const descricao =
      l.selecao.tipo === 'existente' ? l.selecao.opcao.nome ?? l.selecao.opcao.rotulo : l.selecao.descricao;
    if (descricao.trim() === '') continue;

    const desconto: DescontoEntrada | null =
      l.descontoValor > 0 ? { tipo: l.descontoTipo, valor: l.descontoValor } : null;

    itens.push({
      tipo: l.tipo,
      referenciaId: l.selecao.tipo === 'existente' ? l.selecao.opcao.id : null,
      descricao,
      // Peça nova cadastrada por código + nome: preserva o código digitado.
      codigo: l.selecao.tipo === 'novo' ? l.selecao.codigo ?? null : null,
      quantidade: l.quantidade,
      precoUnitario: l.precoUnitario,
      desconto,
      colaboradorId: colaboradoresPorLinha.get(l.uid) ?? null,
    });
  }
  return itens;
}

function toOpcaoCliente(c: ClienteResumo): OpcaoTypeahead {
  const doc = c.documento ? ` (${c.documento})` : '';
  return {
    id: c.id,
    rotulo: `${c.nome}${doc}`,
    busca: `${c.nome} ${c.documento ?? ''}`,
  };
}

function toOpcaoColaborador(c: ColaboradorResumo): OpcaoTypeahead {
  const cargo = c.cargo ? ` - ${c.cargo}` : '';
  return {
    id: c.id,
    rotulo: `${c.nome}${cargo}`,
    busca: `${c.nome} ${c.cargo ?? ''} ${c.email ?? ''} ${c.telefone ?? ''}`,
  };
}

function ordenarPorNomeECodigo(
  nomeA: string,
  codigoA: string,
  nomeB: string,
  codigoB: string,
): number {
  const porNome = nomeA.localeCompare(nomeB, 'pt-BR', { numeric: true, sensitivity: 'base' });
  if (porNome !== 0) return porNome;
  return codigoA.localeCompare(codigoB, 'pt-BR', { numeric: true, sensitivity: 'base' });
}

function toOpcaoProduto(p: ProdutoResumo): OpcaoTypeahead {
  // Peças com estoque 0/negativo continuam selecionáveis — o estado do estoque
  // é apenas informado na linha, não bloqueia a seleção.
  return {
    id: p.id,
    rotulo: `${p.codigo} - ${p.nome}`,
    busca: `${p.codigo} ${p.nome}`,
    codigo: p.codigo,
    nome: p.nome,
    precoUnitario: p.precoUnitario,
    estoqueQuantidade: p.estoqueQuantidade,
  };
}

function toOpcaoServico(s: ServicoResumo): OpcaoTypeahead {
  return {
    id: s.id,
    rotulo: `${s.nome} - ${s.codigo}`,
    busca: `${s.codigo} ${s.nome}`,
    precoUnitario: s.precoUnitario,
  };
}

function CardTotalSecao({ titulo, totais }: { titulo: string; totais: TotaisSecao }) {
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
      </div>
      <div className="card__body totais-rodape">
        <div className="totais-rodape__linha">
          <span>Subtotal</span>
          <strong>{formatadorMoeda.format(totais.bruto)}</strong>
        </div>
        <div className="totais-rodape__linha">
          <span>Desconto</span>
          <strong>− {formatadorMoeda.format(totais.desconto)}</strong>
        </div>
        <div className="totais-rodape__linha totais-rodape__total">
          <span>Total</span>
          <strong>{formatadorMoeda.format(totais.comDesconto)}</strong>
        </div>
      </div>
    </div>
  );
}

type SecaoProps = {
  titulo: string;
  tipo: TipoItemOrcamento;
  linhas: LinhaItem[];
  opcoes: OpcaoTypeahead[];
  colaboradores: OpcaoTypeahead[];
  erro?: string | null;
  onSelecionar: (linha: LinhaItem, valor: ItemTypeaheadValor | null) => void;
  onAtualizar: (uid: string, patch: Partial<LinhaItem>) => void;
  onRemover: (uid: string) => void;
  onAdicionar: () => void;
};

/**
 * Seleção de peça em DOIS campos sincronizados: Código e Nome.
 * - Buscar/escanear pelo código preenche o nome (e vice-versa) — os dois
 *   campos sempre refletem a mesma peça.
 * - Peça não encontrada: o usuário digita o nome no segundo campo; o código
 *   digitado é preservado e a peça nova é criada com ele ao salvar.
 * Reaproveita o ItemTypeahead com duas listas derivadas (uma indexada por
 * código, outra por nome).
 */
function PecaCodigoNome({
  opcoes,
  valor,
  onChange,
}: {
  opcoes: OpcaoTypeahead[];
  valor: ItemTypeaheadValor | null;
  onChange: (v: ItemTypeaheadValor | null) => void;
}) {
  const opcoesCodigo = useMemo(
    () => opcoes.map((o) => ({ ...o, rotulo: o.codigo ?? o.rotulo, busca: o.codigo ?? o.busca })),
    [opcoes],
  );
  const opcoesNome = useMemo(
    () => opcoes.map((o) => ({ ...o, rotulo: o.nome ?? o.rotulo, busca: o.nome ?? o.busca })),
    [opcoes],
  );

  const valorCodigo: ItemTypeaheadValor | null =
    valor?.tipo === 'existente'
      ? { tipo: 'existente', opcao: opcoesCodigo.find((o) => o.id === valor.opcao.id) ?? valor.opcao }
      : valor?.tipo === 'novo' && valor.codigo
        ? { tipo: 'novo', descricao: valor.codigo }
        : null;
  const valorNome: ItemTypeaheadValor | null =
    valor?.tipo === 'existente'
      ? { tipo: 'existente', opcao: opcoesNome.find((o) => o.id === valor.opcao.id) ?? valor.opcao }
      : valor?.tipo === 'novo' && valor.descricao
        ? { tipo: 'novo', descricao: valor.descricao }
        : null;

  function aoMudarCodigo(v: ItemTypeaheadValor | null) {
    if (v?.tipo === 'existente') {
      onChange(v);
      return;
    }
    // Texto livre no campo código = código de barras de uma peça nova.
    const codigo = v?.tipo === 'novo' ? v.descricao.trim() : '';
    const nomeAtual = valor?.tipo === 'novo' ? valor.descricao : '';
    if (!codigo && !nomeAtual) {
      onChange(null);
      return;
    }
    onChange({ tipo: 'novo', descricao: nomeAtual, codigo: codigo || undefined });
  }

  function aoMudarNome(v: ItemTypeaheadValor | null) {
    if (v?.tipo === 'existente') {
      onChange(v);
      return;
    }
    const nome = v?.tipo === 'novo' ? v.descricao : '';
    const codigoAtual = valor?.tipo === 'novo' ? valor.codigo : undefined;
    if (!nome && !codigoAtual) {
      onChange(null);
      return;
    }
    onChange({ tipo: 'novo', descricao: nome, codigo: codigoAtual });
  }

  return (
    <>
      <Field rotulo="Código" dica="Escaneie ou digite o código de barras.">
        {() => (
          <ItemTypeahead
            ariaLabel="Código da peça"
            opcoes={opcoesCodigo}
            valor={valorCodigo}
            onChange={aoMudarCodigo}
            placeholder="Código…"
            mensagemVazio="Sem peça com esse código — digite o nome ao lado para cadastrar."
          />
        )}
      </Field>
      <Field rotulo="Nome da peça">
        {() => (
          <ItemTypeahead
            ariaLabel="Nome da peça"
            opcoes={opcoesNome}
            valor={valorNome}
            onChange={aoMudarNome}
            placeholder="Buscar ou cadastrar peça…"
          />
        )}
      </Field>
    </>
  );
}

function SecaoItens({
  titulo,
  tipo,
  linhas,
  opcoes,
  colaboradores,
  erro,
  onSelecionar,
  onAtualizar,
  onRemover,
  onAdicionar,
}: SecaoProps) {
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
        <Button onClick={onAdicionar}>+ Adicionar {tipo === 'Produto' ? 'peça' : 'serviço'}</Button>
      </div>
      <div className="card__body">
        {erro && (
          <div className="form-error form-error--secao" role="alert">
            {erro}
          </div>
        )}

        <ol className="orcamento-itens-lista">
          {linhas.map((l, idx) => {
            const sub = arred(l.quantidade * l.precoUnitario);
            const valorDesc = arred(calcularDesconto(sub, l.descontoTipo, l.descontoValor));
            const subFinal = arred(sub - valorDesc);
            // Estoque atual da peça selecionada — só para INFORMAR o estado
            // quando está zerado/negativo; não bloqueia nem limita a quantidade.
            const estoqueAtualPeca =
              tipo === 'Produto' && l.selecao?.tipo === 'existente'
                ? l.selecao.opcao.estoqueQuantidade
                : undefined;
            const semEstoquePeca = estoqueAtualPeca !== undefined && estoqueAtualPeca <= 0;
            // Peça nova: código informado mas nome ainda em branco.
            const codigoNovoSemNome =
              tipo === 'Produto' &&
              l.selecao?.tipo === 'novo' &&
              Boolean(l.selecao.codigo?.trim()) &&
              l.selecao.descricao.trim() === ''
                ? l.selecao.codigo!.trim()
                : null;
            return (
              <li key={l.uid} className="orcamento-item">
                <div className="orcamento-item__cabecalho">
                  <span className="itens-cards__indice">
                    {tipo === 'Produto' ? 'Peça' : 'Serviço'} {idx + 1}
                  </span>
                  <Button
                    className="btn--danger"
                    aria-label={`Remover linha ${l.uid}`}
                    onClick={() => onRemover(l.uid)}
                  >
                    Remover
                  </Button>
                </div>

                <div
                  className={`orcamento-item__principal${
                    tipo === 'Produto' ? ' orcamento-item__principal--peca' : ''
                  }`}
                >
                  {tipo === 'Produto' ? (
                    <PecaCodigoNome
                      opcoes={opcoes}
                      valor={l.selecao}
                      onChange={(v) => onSelecionar(l, v)}
                    />
                  ) : (
                    <Field rotulo="Serviço">
                      {() => (
                        <ItemTypeahead
                          ariaLabel="Serviço"
                          opcoes={opcoes}
                          valor={l.selecao}
                          onChange={(v) => onSelecionar(l, v)}
                          placeholder="Buscar ou cadastrar serviço…"
                        />
                      )}
                    </Field>
                  )}
                  <Field rotulo="Colaborador">
                    {() => (
                      <ItemTypeahead
                        ariaLabel="Colaborador do item"
                        opcoes={colaboradores}
                        valor={l.colaborador}
                        onChange={(v) => onAtualizar(l.uid, { colaborador: v })}
                        placeholder="Buscar ou cadastrar colaborador..."
                        mensagemVazio="Digite o nome para cadastrar um novo colaborador."
                      />
                    )}
                  </Field>
                </div>

                {codigoNovoSemNome && (
                  <p className="orcamento-item__aviso-estoque" role="status">
                    Código novo “{codigoNovoSemNome}” informado. Digite o nome da peça para
                    cadastrá-la.
                  </p>
                )}

                {semEstoquePeca && (
                  <p className="orcamento-item__aviso-estoque" role="status">
                    {estoqueAtualPeca! < 0
                      ? `Atenção: peça com estoque negativo (atual: ${estoqueAtualPeca!.toLocaleString('pt-BR')}).`
                      : 'Atenção: peça sem estoque (atual: 0).'}{' '}
                    Pode ser usada normalmente; a ordem de serviço será salva assim mesmo.
                  </p>
                )}

                <div className="orcamento-item__valores">
                  <Field rotulo="Qtd">
                    {(id) => (
                      <input
                        id={id}
                        aria-label="Quantidade"
                        className="input"
                        type="number"
                        step="1"
                        min="0"
                        value={l.quantidade}
                        onChange={(e) =>
                          onAtualizar(l.uid, {
                            quantidade: inteiroNaoNegativo(Number(e.target.value)),
                          })
                        }
                      />
                    )}
                  </Field>
                  <Field rotulo="Preço unit.">
                    {() => (
                      <InputMonetario
                        ariaLabel="Preço unitário"
                        valor={l.precoUnitario}
                        onChange={(v) => onAtualizar(l.uid, { precoUnitario: v })}
                      />
                    )}
                  </Field>
                  <Field rotulo="Desc.">
                    {() => (
                      <div className="orcamento-item__desconto">
                        <select
                          aria-label="Tipo do desconto"
                          className="input"
                          value={l.descontoTipo}
                          onChange={(e) =>
                            onAtualizar(l.uid, { descontoTipo: e.target.value as TipoDesconto })
                          }
                        >
                          <option value="Percentual">%</option>
                          <option value="Valor">R$</option>
                        </select>
                        <InputMonetario
                          ariaLabel="Valor do desconto"
                          valor={l.descontoValor}
                          onChange={(v) => onAtualizar(l.uid, { descontoValor: v })}
                        />
                      </div>
                    )}
                  </Field>
                  <div className="orcamento-item__subtotal">
                    <span className="field__label">Subtotal</span>
                    <strong>{formatadorMoeda.format(subFinal)}</strong>
                    {valorDesc > 0 && (
                      <span className="field__hint">
                        Desconto: {formatadorMoeda.format(valorDesc)}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
